import React, { useMemo, useState, useEffect } from 'react';
import { useReportContext } from '../../context/DetailContext';
import styled from 'styled-components';
import { ACommonList } from '../../sidebar';
import LoanInfoService from '../../service/LoanInfo';
import { useUser } from '../../context/UserContext';
import { useModal } from '../../context/ModalContext';
import RegistryModal from './components/registryDocument/RegistryModal';
import { isFinalMonth } from '../../utils/auth';

const LockItem = ['건물 등기', '토지 등기', '전입세대열람원'];

const AttachmentFile = ({
  linkSet,
  caseInfo,
  setShowFileModal,
}: {
  linkSet?: any;
  caseInfo?: any;
  setShowFileModal?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { type, regionAccess, quotaAccess } = useReportContext();
  const { user } = useUser();
  const { modalDispatch } = useModal();

  const [openRegistryModal, setOpenRegistryModal] = useState(false);
  const [registryType, setRegistryType] = useState<string>('');
  const [pdfLink, setPdfLink] = useState<string | null>(null);

  const [itemDetailButtonOn, setItemDetailButtonOn] = useState<boolean>(false);
  const [casDetailButtonOn, setCasDetailButtonOn] = useState<boolean>(false);
  const [dateDetailButtonOn, setDateDetailButtonOn] = useState<boolean>(false);
  const [deliveryButtonOn, setDeliveryButtonOn] = useState<boolean>(false);
  const [statusServeryButtonOn, setStatusServeryButtonOn] =
    useState<boolean>(false);
  const iidx = caseInfo?.iidx;
  // 버튼 리스트 구성
  const fileButtons = useMemo(() => {
    return [
      {
        title: '매각물건명세서',
        link: linkSet?.buttonLink?.assetDetail || '',
      },
      {
        title: '감정평가서',
        link: linkSet?.buttonLink?.appraisal || '',
      },
      {
        title: '현황조사서',
        link:
          statusServeryButtonOn && iidx
            ? linkSet?.buttonLink?.statusServery || `/statusServey?iidx=${iidx}`
            : '',
      },
      {
        title: '건물 등기',
        link: linkSet.buttonLink.buildingRegister || '',
      },
      {
        title: '토지 등기',
        link: linkSet.buttonLink.landRegister || '',
      },
      {
        title: '전입세대열람원',
        link: linkSet.buttonLink.buildingCertificate || '',
      },
      {
        title: '물건 상세',
        link: iidx ? `/itemDetail?iidx=${iidx}` : '',
        disabled: !itemDetailButtonOn,
      },
      {
        title: '사건내역',
        link: iidx ? `/incidentDetail?iidx=${iidx}` : '',
        disabled: !casDetailButtonOn,
      },
      {
        title: '기일내역',
        link: iidx ? `/dateDetail?iidx=${iidx}` : '',
        disabled: !dateDetailButtonOn,
      },
      {
        title: '문건/송달 내역',
        link:
          deliveryButtonOn && iidx
            ? linkSet?.buttonLink?.documentsHistory ||
              `/documentDetail?iidx=${caseInfo.iidx}`
            : '',
        disabled: !deliveryButtonOn,
      },
    ];
  }, [
    linkSet,
    iidx,
    itemDetailButtonOn,
    casDetailButtonOn,
    dateDetailButtonOn,
    statusServeryButtonOn,
    deliveryButtonOn,
  ]);

  // API 호출: 상세 버튼 상태
  useEffect(() => {
    if (type === 's') return;
    if (iidx) {
      getDetailButtonStates(iidx);
    }
  }, [type, iidx]);

  const getDetailButtonStates = async (iidx: number) => {
    try {
      const res = await LoanInfoService.courtDetail(iidx);
      if (res.data.item_detail?.basic_info?.case_no != null) {
        setItemDetailButtonOn(true);
      }
      if (res.data.case_detail?.basic_info?.case_no != null) {
        setCasDetailButtonOn(true);
      }
      if (res.data.date_list != null) {
        setDateDetailButtonOn(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // API 호출: 현황조사서 / 문건송달
  useEffect(() => {
    if (iidx) {
      getStatusServeyData(iidx);
      getDeliveryData(iidx);
    }
  }, [iidx]);

  const getStatusServeyData = async (iidx: number) => {
    try {
      const res = await LoanInfoService.statusServey(iidx);
      setStatusServeryButtonOn(!!res.data?.top);
    } catch (e) {
      console.error(e);
      setStatusServeryButtonOn(false);
    }
  };

  const getDeliveryData = async (iidx: number) => {
    try {
      const res = await LoanInfoService.documentDelivery(iidx);
      const hasData =
        res.data?.date_list?.length > 0 && res.data?.date_list2?.length > 0;
      setDeliveryButtonOn(hasData);
    } catch (e) {
      console.error(e);
      setDeliveryButtonOn(false);
    }
  };

  return (
    <div>
      <DetailContainer id="첨부파일">
        <TitleWrapper>
          <AttchmentTitle>첨부파일</AttchmentTitle>
          <ExportButton onClick={() => setShowFileModal?.(true)}>
            일괄 내보내기
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </ExportButton>
        </TitleWrapper>

        <ContentWrapper>
          {fileButtons.map((item, idx) => {
            const isDisabled = !item.link || item.disabled;
            return (
              <ContentBox
                key={idx}
                onClick={() => {
                  if (isDisabled) return;

                  if (!item.link) return;

                  // 무료회원
                  if (LockItem.includes(item.title) && !regionAccess) {
                    if (user?.plan_grade) {
                      modalDispatch({ type: 'USER_ALERT', payload: 'local' });
                    } else {
                      modalDispatch({ type: 'USER_ALERT', payload: 'free' });
                    }
                    return;
                  }

                  const isPremium = user?.plan_grade === 'P';
                  setPdfLink(item.link);

                  if (
                    (item.title === '건물 등기' ||
                      item.title === '토지 등기') &&
                    !isPremium
                  ) {
                    setOpenRegistryModal(true);
                    setRegistryType('G');
                    return;
                  }
                  if (item.title === '전입세대열람원' && !isPremium) {
                    setOpenRegistryModal(true);
                    setRegistryType('H');
                    return;
                  }

                  // 이용권 만료
                  if (LockItem.includes(item.title) && !quotaAccess) {
                    if (isFinalMonth(user?.plan_end)) {
                      modalDispatch({
                        type: 'USER_ALERT',
                        payload: 'quotaDepletedExpiring',
                      });
                    } else {
                      modalDispatch({
                        type: 'USER_ALERT',
                        payload: 'quotaDepleted',
                      });
                    }
                    return;
                  }

                  window.open(item.link, '_blank');
                }}
                style={{
                  opacity: isDisabled ? 0.2 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Item>
                  {!regionAccess && LockItem.includes(item.title) && (
                    <img src="/assets/images/icon/lock.svg" alt="" />
                  )}
                  {item.title}
                </Item>

                <img
                  src="/assets/images/arrow/arrow-up-black.svg"
                  alt=""
                  style={{ transform: 'rotate(90deg)' }}
                />
              </ContentBox>
            );
          })}
        </ContentWrapper>
        {openRegistryModal && pdfLink && (
          <RegistryModal
            link={pdfLink}
            type={registryType}
            onClose={() => setOpenRegistryModal(false)}
          />
        )}
      </DetailContainer>
    </div>
  );
};

export default AttachmentFile;

const DetailContainer = styled.div`
  width: 100%;
  height: fit-content;
  padding: 24px 16px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AttchmentTitle = styled.div`
  color: #222222;
  font-size: 16px;
  font-weight: 700;
`;

const ExportButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const ContentWrapper = styled.div`
  padding-left: 16px;
  padding-right: 8px;
  border-radius: 8px;
  background-color: #f7f8fc;
`;

const ContentBox = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f3f5f7;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
